/**
 * Similar Profiles Component
 * Displays matching profiles when user enters a stage name
 * Includes options to claim profile or reset password
 * 
 * @author Varun Pandey
 */

import React, { useState } from 'react';
import { User, KeyRound, ChevronRight, ChevronLeft, X, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Loader } from '../ui';

export interface SimilarProfile {
  id: string;
  /** Display name (API: professional_name) */
  stageName: string;
  fullName?: string;
  category?: string;
  /** API: profile_pic */
  profileImage?: string;
  isVerified?: boolean;
  /** Masked email from API */
  masked_email?: string;
  /** Masked phone from API */
  masked_phone?: string;
  /** Artist slug for claim flow (from API) */
  slug?: string;
}

interface SimilarProfilesProps {
  profiles: SimilarProfile[];
  isLoading?: boolean;
  onClaimProfile: (profile: SimilarProfile) => void;
  onResetPassword: (profile: SimilarProfile) => void;
  onDismiss?: () => void;
  className?: string;
  page?: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

export const SimilarProfiles: React.FC<SimilarProfilesProps> = ({
  profiles,
  isLoading = false,
  onClaimProfile,
  onResetPassword,
  onDismiss,
  className,
  page,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const [expandedProfileId, setExpandedProfileId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className={cn('rounded-[0.75rem]  p-4', className)}>
        <div className="flex justify-center items-center gap-3">
          <Loader size="small" />
        </div>
      </div>
    );
  }

  if (profiles.length === 0) {
    return null;
  }

  const toggleProfile = (profileId: string) => {
    setExpandedProfileId(expandedProfileId === profileId ? null : profileId);
  };

  const currentPage = page ?? 1;
  const effectivePageSize = pageSize ?? (profiles.length || 10);
  const effectiveTotalCount = totalCount ?? profiles.length;
  const totalPages = Math.ceil(effectiveTotalCount / effectivePageSize);

  return (
    <div className={cn('rounded-[0.75rem]  overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-2 border-b border-[#2C2C2E] bg-[#1C1C1E]">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F33363]/20 flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-[#F33363]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">
              {profiles.length === 1
                ? 'Found a similar profile'
                : `Found ${profiles.length} similar profiles`}
            </h3>
            <p className="text-xs text-[#8E8E93] mt-0.5">
              Is one of these yours? You can claim it or reset your password.
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-[#8E8E93] hover:text-white transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Profiles List */}
      <div
        className={cn(
          'max-h-[40vh] overflow-y-auto',
          profiles.length > 3 && 'scrollbar-thin scrollbar-thumb-[#3A3A3C] scrollbar-track-transparent'
        )}
      >
        {profiles.map((profile, _index) => (
          <div
            key={profile.id}
            className={cn(
              'border-b border-[#2C2C2E] last:border-b-0',
              expandedProfileId === profile.id && 'bg-[#2C2C2E]/30'
            )}
          >
            {/* Profile Row */}
            <button
              onClick={() => toggleProfile(profile.id)}
              className="w-full flex items-center gap-3 p-2 hover:bg-[#2C2C2E]/50 transition-colors text-left"
            >
              {/* Avatar */}
              {profile.profileImage ? (
                <img
                  src={profile.profileImage}
                  alt={profile.stageName}
                  className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#2C2C2E] flex-shrink-0">
                  <User className="h-6 w-6 text-[#8E8E93]" />
                </div>
              )}

              {/* Profile Info */}
              <div className="flex flex-col w-full">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-white truncate">
                    {profile.stageName}
                  </h4>
                  {profile.isVerified && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-500">
                      <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8E8E93] truncate">
                  {[profile.masked_email, profile.masked_phone].filter(Boolean).join(' • ') ||
                    [profile.fullName, profile.category].filter(Boolean).join(' • ') ||
                    '—'}
                </p>
              </div>

              {/* Expand Icon */}
              <ChevronRight
                className={cn(
                  'h-5 w-5 text-[#8E8E93] transition-transform flex-shrink-0',
                  expandedProfileId === profile.id && 'rotate-90'
                )}
              />
            </button>

            {/* Expanded Actions */}
            {expandedProfileId === profile.id && (
              <div className="px-4 pb-4 space-y-2">
                <p className="text-xs text-[#8E8E93] mb-3">
                  Choose an action for this profile:
                </p>
                <div className="flex flex-row justify-center items-center gap-2">
                  {/* Claim Profile Button */}
                  <button
                    onClick={() => onClaimProfile(profile)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] transition-colors text-left"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F33363]/20 flex-shrink-0">
                      <User className="h-5 w-5 text-[#F33363]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-white">Claim This Profile</h5>

                    </div>
                    <ChevronRight className="h-4 w-4 text-[#8E8E93] flex-shrink-0" />
                  </button>

                  {/* Reset Password Button */}
                  <button
                    onClick={() => onResetPassword(profile)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] transition-colors text-left"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20 flex-shrink-0">
                      <KeyRound className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-medium text-white">Reset Password</h5>

                    </div>
                    <ChevronRight className="h-4 w-4 text-[#8E8E93] flex-shrink-0" />
                  </button>
                </div>

              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination (Jobs-style) */}
      {effectiveTotalCount > 0 && totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-center gap-2 pt-2 pb-1 border-t border-[#2C2C2E]/60 bg-transparent backdrop-blur-md">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1 || isLoading}
            className={cn(
              'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[3.5rem]',
              currentPage <= 1 || isLoading
                ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
            )}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            <span>Prev</span>
          </button>

          {(() => {
            const WINDOW_SIZE = 5;
            let startPage = 1;
            let endPage = totalPages;

            if (totalPages > WINDOW_SIZE) {
              if (currentPage <= 3) {
                startPage = 1;
                endPage = WINDOW_SIZE;
              } else if (currentPage >= totalPages - 2) {
                endPage = totalPages;
                startPage = totalPages - (WINDOW_SIZE - 1);
              } else {
                startPage = currentPage - 2;
                endPage = currentPage + 2;
              }
            }

            const pages: number[] = [];
            for (let p = startPage; p <= endPage; p += 1) pages.push(p);

            return (
              <div className="flex items-center gap-1">
                {pages.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onPageChange(p)}
                    disabled={isLoading}
                    className={cn(
                      'min-w-[1.9rem] h-7 px-1.5 rounded-md text-[11px] font-bold border transition-colors flex items-center justify-center',
                      p === currentPage
                        ? 'border-[#F33363] bg-[#F33363] text-white'
                        : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]',
                      isLoading && 'opacity-70 cursor-not-allowed'
                    )}
                    aria-label={`Page ${p}`}
                    aria-current={p === currentPage ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}
              </div>
            );
          })()}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages || isLoading}
            className={cn(
              'flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors min-w-[3.5rem]',
              currentPage >= totalPages || isLoading
                ? 'border-[#3A3A3C] text-[#8E8E93] cursor-not-allowed opacity-60'
                : 'border-[#3A3A3C] bg-[#1C1C1E] text-white hover:border-[#F33363]/50 hover:bg-[#252527]'
            )}
            aria-label="Next page"
          >
            <span>Next</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Footer hint for many profiles */}
      {profiles.length > 3 && (
        <div className="p-1">
          <p className="text-xs text-[#8E8E93] text-center">
            Scroll to see more profiles • Tap to expand
          </p>
        </div>
      )}
    </div>
  );
};
